import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import useTheme from '../theme/useTheme';
import Avatar from '../ui/Avatar';
import Btn from '../ui/Btn';
import './shell.css';

/* Session storage can throw in locked-down browsers — never let the bar die for it. */
function ss(key) {
  try {
    return sessionStorage.getItem(key) || '';
  } catch (e) {
    return '';
  }
}

/* appCountries is stored JSON-stringified, but older sessions hold a plain string. */
function countriesLabel() {
  const raw = ss('appCountries');
  if (!raw) return '';
  let value = raw;
  try {
    value = JSON.parse(raw);
  } catch (e) {
    /* not JSON — use the raw string */
  }
  if (Array.isArray(value)) {
    // Entries arrive as {label:"Greece", value:10} — join the labels, not the objects.
    return value
      .map((item) => {
        if (!item) return '';
        if (typeof item === 'string') return item;
        return item.label || item.name || item.Country || '';
      })
      .filter(Boolean)
      .join(', ');
  }
  if (value && typeof value === 'object') return '';
  return String(value || '');
}

function sportLabel() {
  return ss('SportID') === '2' ? 'Basketball' : 'Football';
}

export default function TopBar({ title, eyebrow }) {
  const { isDark, toggle } = useTheme();
  const { userName, setIsAuth } = useContext(UserContext);
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const photo = ss('usrPhoto');
  const name = userName || ss('userName') || 'User';
  const countries = countriesLabel();
  const role = countries ? 'Manager · ' + countries : 'Manager';

  useEffect(() => {
    if (!open) return undefined;

    const onDown = (ev) => {
      if (wrapRef.current && !wrapRef.current.contains(ev.target)) setOpen(false);
    };
    const onKey = (ev) => {
      if (ev.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  /* Same keys DashboardNav.js clears — do not add or remove any. */
  const logout = () => {
    setOpen(false);
    setIsAuth(false);
    sessionStorage.setItem('isAuth', 0);
    sessionStorage.removeItem('isAuth');
    sessionStorage.removeItem('userToken');
    sessionStorage.removeItem('userName');
    sessionStorage.removeItem('mngapiToken');
    sessionStorage.removeItem('ProductTitle');
    sessionStorage.removeItem('SportID');
    sessionStorage.removeItem('appCountries');
    navigate('/login');
  };

  return (
    <header className="topbar">
      <div className="crumb">
        <h1>{title || 'Dashboard'}</h1>
        <span className="mono">{eyebrow || sportLabel()}</span>
      </div>

      <div className="topbar-actions">
        <Btn className="theme-btn" onClick={toggle} aria-label="Switch theme">
          {isDark ? (
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="4" />
              <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
            </svg>
          )}
          <span className="theme-btn-label">{isDark ? 'Light' : 'Dark'}</span>
        </Btn>

        <Btn variant="accent" onClick={() => navigate('/list')}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 5v14M5 12h14" />
          </svg>
          <span className="newlist-label">New List</span>
        </Btn>

        <div className="who" ref={wrapRef}>
          <button
            type="button"
            className="who-btn"
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            <Avatar src={photo} name={name} size={32} />

            <span className="who-text">
              <span className="who-name" title={name}>{name}</span>
              <span className="who-role" title={role}>{role}</span>
            </span>

            <svg className="who-caret" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>

          {open && (
            <div className="menu" role="menu" aria-label="Account">
              <Link className="menu-item" role="menuitem" to="/profile" onClick={() => setOpen(false)}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="12" cy="8" r="3.5" />
                  <path d="M4.5 20c0-3.6 3.4-6 7.5-6s7.5 2.4 7.5 6" />
                </svg>
                My Profile
              </Link>

              <Link className="menu-item" role="menuitem" to="/plans" onClick={() => setOpen(false)}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="M3 10h18M7 15h4" />
                </svg>
                Plans
              </Link>

              <div className="menu-sep" />

              <button type="button" className="menu-item menu-item-crit" role="menuitem" onClick={logout}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M15 17l5-5-5-5M20 12H9M12 20H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h6" />
                </svg>
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
